/**
 * Student-housing Additional Questions on the Rental step.
 * Yes/No markup matches shared AdditionalYesNoRow (N4/L1 / Employment).
 */
import {
	AdditionalExplainField,
	AdditionalQuestionsCard,
	AdditionalYesNoRow,
} from '#app/components/screening/additional-question-ui'

export type RentalAdditionalQuestionsVariant =
	| 'full' // currently / previously rented — all 3
	| 'first-renter' // first-time — last 2 only

export type RentalAdditionalAnswers = {
	everEvicted: string
	everEvictedExplanation: string
	everConvictedFelony: string
	everConvictedFelonyExplanation: string
	doesSmoke: string
}

export type RentalAdditionalErrors = {
	everEvicted?: string[]
	everEvictedExplanation?: string[]
	everConvictedFelony?: string[]
	everConvictedFelonyExplanation?: string[]
	doesSmoke?: string[]
}

export const FELONY_LABEL =
	'Have You Ever Been Convicted of a Felony That Involved an Offense Against Property, Persons, Government Officials, or That Involved Firearms, Illegal Drugs, or Sex or Sex Crimes?'

export function pickRentalAdditionalAnswers(
	source: RentalAdditionalAnswers,
): RentalAdditionalAnswers {
	return {
		everEvicted: source.everEvicted,
		everEvictedExplanation: source.everEvictedExplanation,
		everConvictedFelony: source.everConvictedFelony,
		everConvictedFelonyExplanation: source.everConvictedFelonyExplanation,
		doesSmoke: source.doesSmoke,
	}
}

export function pickRentalAdditionalErrors(
	source: RentalAdditionalErrors,
): RentalAdditionalErrors {
	return {
		everEvicted: source.everEvicted,
		everEvictedExplanation: source.everEvictedExplanation,
		everConvictedFelony: source.everConvictedFelony,
		everConvictedFelonyExplanation: source.everConvictedFelonyExplanation,
		doesSmoke: source.doesSmoke,
	}
}

export function appendRentalAdditionalFormData(
	submitData: FormData,
	answers: RentalAdditionalAnswers,
	variant: RentalAdditionalQuestionsVariant,
	enabledKeys?: readonly string[],
) {
	const on = (key: string) => !enabledKeys || enabledKeys.includes(key)
	if (variant === 'full' && on('ever_evicted')) {
		submitData.append('everEvicted', answers.everEvicted)
		submitData.append(
			'everEvictedExplanation',
			answers.everEvicted === 'yes' ? answers.everEvictedExplanation : '',
		)
	}
	if (on('felony_conviction')) {
		submitData.append('everConvictedFelony', answers.everConvictedFelony)
		submitData.append(
			'everConvictedFelonyExplanation',
			answers.everConvictedFelony === 'yes'
				? answers.everConvictedFelonyExplanation
				: '',
		)
	}
	if (on('do_you_smoke')) {
		submitData.append('doesSmoke', answers.doesSmoke)
	}
}

export function validateRentalAdditionalQuestions(
	answers: RentalAdditionalAnswers,
	variant: RentalAdditionalQuestionsVariant,
	enabledKeys?: readonly string[],
): RentalAdditionalErrors {
	const on = (key: string) => !enabledKeys || enabledKeys.includes(key)
	const errors: RentalAdditionalErrors = {}
	if (variant === 'full' && on('ever_evicted')) {
		if (!answers.everEvicted) {
			errors.everEvicted = ['Please answer this question']
		} else if (
			answers.everEvicted === 'yes' &&
			!answers.everEvictedExplanation.trim()
		) {
			errors.everEvictedExplanation = ['Please provide details']
		}
	}
	if (on('felony_conviction')) {
		if (!answers.everConvictedFelony) {
			errors.everConvictedFelony = ['Please answer this question']
		} else if (
			answers.everConvictedFelony === 'yes' &&
			!answers.everConvictedFelonyExplanation.trim()
		) {
			errors.everConvictedFelonyExplanation = ['Please provide details']
		}
	}
	if (on('do_you_smoke')) {
		if (!answers.doesSmoke) {
			errors.doesSmoke = ['Please answer this question']
		}
	}
	return errors
}

export function RentalAdditionalQuestions({
	variant,
	values,
	errors,
	onChange,
	enabledKeys,
	/** When true, skip outer card — parent already wraps Additional Questions. */
	embedded = false,
}: {
	variant: RentalAdditionalQuestionsVariant
	values: RentalAdditionalAnswers
	errors?: RentalAdditionalErrors
	onChange: (field: keyof RentalAdditionalAnswers, value: string) => void
	/** Catalog prebuilt keys; omit to show all (legacy). */
	enabledKeys?: readonly string[]
	embedded?: boolean
}) {
	const on = (key: string) => !enabledKeys || enabledKeys.includes(key)
	const showEvicted = variant === 'full' && on('ever_evicted')
	const showFelony = on('felony_conviction')
	const showSmoke = on('do_you_smoke')
	if (!showEvicted && !showFelony && !showSmoke) return null

	const body = (
		<div className="space-y-3">
			{showEvicted ? (
				<AdditionalYesNoRow
					name="everEvicted"
					label="Have You Ever Been Evicted?"
					value={values.everEvicted}
					error={errors?.everEvicted?.[0]}
					onChange={(v) => onChange('everEvicted', v)}
				>
					{values.everEvicted === 'yes' ? (
						<AdditionalExplainField
							id="ever-evicted-explanation"
							value={values.everEvictedExplanation}
							error={errors?.everEvictedExplanation}
							onChange={(v) => onChange('everEvictedExplanation', v)}
						/>
					) : null}
				</AdditionalYesNoRow>
			) : null}

			{showFelony ? (
				<AdditionalYesNoRow
					name="everConvictedFelony"
					label={FELONY_LABEL}
					value={values.everConvictedFelony}
					error={errors?.everConvictedFelony?.[0]}
					onChange={(v) => onChange('everConvictedFelony', v)}
				>
					{values.everConvictedFelony === 'yes' ? (
						<AdditionalExplainField
							id="ever-convicted-felony-explanation"
							value={values.everConvictedFelonyExplanation}
							error={errors?.everConvictedFelonyExplanation}
							onChange={(v) => onChange('everConvictedFelonyExplanation', v)}
						/>
					) : null}
				</AdditionalYesNoRow>
			) : null}

			{showSmoke ? (
				<AdditionalYesNoRow
					name="doesSmoke"
					label="Do You Smoke?"
					value={values.doesSmoke}
					error={errors?.doesSmoke?.[0]}
					onChange={(v) => onChange('doesSmoke', v)}
				/>
			) : null}
		</div>
	)

	if (embedded) return body
	return <AdditionalQuestionsCard>{body}</AdditionalQuestionsCard>
}
